/* eslint-disable max-lines */

/* eslint-disable complexity */
import _ from 'lodash';
import { matchPath } from 'react-router-dom';
import { getGrowthBookFeatureFlag } from 'src/featureFlags';
import {
  getClientCode,
  getCurrentUser,
  getDashboardSections,
  getFeaturesConfig,
  getLeadModules,
  getRevyreWorkflowConfig,
} from 'src/models/lmsConfig/queries';
import { HOME_DASHBOARD } from 'src/modules/constants';
import type { ModuleTypeValue } from 'src/modules/types';
import type { AppDispatch } from 'src/store';
// eslint-disable-next-line vymo-ui/restrict-import
import { normalizePath } from 'src/workspace/queries';
import { renderModule } from 'src/workspace/slice';
import { getAppLocationInfo, getClientConfigData } from 'src/workspace/utils';
// eslint-disable-next-line vymo-ui/restrict-import
import { REVYRE } from '../revyre/constants';
import {
  CLIENT_CONSTANTS,
  getUIModules,
  HOMEPAGE_PATH,
  MENU_GROUP_TYPES,
  MODULE_CODES,
  REVYRE_CONSTANTS,
  REVYRE_PATHS,
  ROLE_CONSTANTS,
  routesWithPath,
  SECTION_CONFIGS,
  SECTION_NAMES,
  SECTION_TYPES,
  sidebarGroupings,
  subMenuMap,
  TYPE_PREFIXES,
  TYPE_STRINGS,
  TYPE_SUFFIXES,
  USER_CONSTANTS,
  workflowDownloadSection,
  workflowRevocationsSection,
  workflowUploadsSection,
} from './constants';
import type { ExtendedNavigationItem } from './types';

export const getActiveMenuFromPath = (selectedMenu, currentPath) => {
  const currentMenuItem = selectedMenu?.menu?.items?.find(
    (item) => item?.path === currentPath,
  );

  return {
    menu: (currentMenuItem ||
      (selectedMenu?.menu?.items?.length
        ? selectedMenu?.menu?.items?.[0]
        : selectedMenu?.menu)) as ExtendedNavigationItem | null,
    type: currentMenuItem ? 'direct' : selectedMenu?.type,
  };
};

export const getActiveMenuFromLocation = (selectedMenu) => {
  const { hash: currentPath } = getAppLocationInfo();
  return getActiveMenuFromPath(selectedMenu, currentPath);
};

export const getBrowserWebViewSections = () => {
  const config = getClientConfigData();
  const menuSections = _.get(config, 'menu.sections.0.section_items', []);
  return _.filter(menuSections, { type: 'browser_webview' });
};

const shouldShowGoalsTab = () => {
  const clientId = getClientCode();
  if (clientId === CLIENT_CONSTANTS.FEC_CLIENT_CODE) {
    const userInfo = getCurrentUser();
    const currentUserRole = _.get(userInfo, 'attributes.role', '');
    if (ROLE_CONSTANTS.ALLOWED_GOALS_ROLES.indexOf(currentUserRole) > -1) {
      return true;
    }
  }
  return _.get(getFeaturesConfig(), 'goals_enabled', false);
};

const shouldShowHomeDashboard = () =>
  true &&
  !_.get(
    _.get(getClientConfigData(), 'dashboard_config'),
    'disable_homepage_for_admin_role',
    false,
  );

export function canEnableTaskSections() {
  const userConfig = _.get(getClientConfigData(), 'user_config', {});
  const leadModules = getLeadModules();

  const userTasksEnabled = _.get(userConfig, 'tasks_enabled', false);
  const hasTaskEnabledModules = !_.isEmpty(
    _.find(leadModules, { tasks_enabled: true }),
  );

  return userTasksEnabled || hasTaskEnabledModules;
}

export const isPerformanceDashboardEnabled = () => {
  const featuresConfig = getFeaturesConfig();
  const isPerformanceEnabled = getGrowthBookFeatureFlag(
    'enable-performance-dashboard',
  );
  return (
    isPerformanceEnabled &&
    _.get(featuresConfig, 'performance_tab_v2.enabled', false)
  );
};
const getCampaigns = () =>
  Object.values(
    _.filter(
      _.get(getClientConfigData(), 'bo_configs', []),
      (item) => item.type !== MODULE_CODES.MASTERS,
    ),
  );

const getMasters = () =>
  Object.values(
    _.filter(
      _.get(getClientConfigData(), 'bo_configs', []),
      (item) => item.type === MODULE_CODES.MASTERS,
    ),
  );

const getItemPath = (type, module) => {
  if (
    type === 'leads' &&
    module !== 'tasks' &&
    getGrowthBookFeatureFlag('enable-new-legacy-leads-list')
  ) {
    return normalizePath(`/${module}/list`);
  }
  if (type === 'crm-updates-status') {
    return normalizePath('/crm-updates-status/list');
  }
  let path = routesWithPath[type]?.path || '';
  path = path.replace(':module', module);
  return `/web2${normalizePath(path)}`;
};

export const getMenuV2 = () => {
  const uiModules = getUIModules();
  const modules = (getLeadModules() || []).filter(
    (module) => module.display !== false,
  );
  const revyreModules = (getRevyreWorkflowConfig() || []).filter(
    (workflow) =>
      workflow.type !== TYPE_STRINGS.HIERARCHY && (workflow.display ?? true),
  );
  const campaigns: any = getCampaigns();
  const masters: any = getMasters();
  const campaignCodes: string[] = (campaigns || []).map(
    (campaign) => campaign.code,
  );
  const masterCodes: string[] = (masters || []).map((master) => master.code);

  const currentUser = getCurrentUser();

  const modifiedRevyreModules = revyreModules?.map((revyreModule) => ({
    name: revyreModule?.name,
    key: `${REVYRE_CONSTANTS.KEY_PREFIX}${revyreModule.code}`,
    code: revyreModule?.code,
    type: revyreModule?.type,
    items: [
      {
        name: revyreModule?.name,
        code: revyreModule?.code,
        key: `${REVYRE_CONSTANTS.KEY_PREFIX}${revyreModule.code}`,
        icon: '',
        display: true,
        type: TYPE_STRINGS.REVYRE,
        path:
          revyreModule?.type === TYPE_STRINGS.WORKFLOW &&
          !REVYRE.COLLECT_IQ.DOMAIN_TYPES.includes(revyreModule.code)
            ? `/${REVYRE_PATHS.WORKFLOW_PREFIX}${revyreModule?.code}`
            : `/${REVYRE_PATHS.PREFIX}${revyreModule?.code}`,
      },
    ],
  }));

  const supersetEnabledStatus = _.get(
    getFeaturesConfig(),
    'superset_enabled',
    false,
  );

  const configDashboardSections = getDashboardSections().filter(
    (section) => section.display !== false,
  );

  if (modifiedRevyreModules?.length) {
    modules.unshift(...modifiedRevyreModules);
    configDashboardSections.push(
      workflowDownloadSection,
      workflowUploadsSection,
      workflowRevocationsSection,
    );
  }

  let settingsEnabled = false;
  let ddpReports = false;
  let activityBulkUploadEnabled = false;
  let commonEnabled = false;
  let reportsEnabled = false;
  let tableauEnabled = false;
  let customizeEnabled = false;
  let approvalEnabled = true;
  let onboardEnabled = false;
  let waiverMatrixEnabled = false;
  const isSupersetEnabled = supersetEnabledStatus;
  const isClientHealthDashboardEnabled =
    currentUser.code === USER_CONSTANTS.SUPER_USER_CODE;
  const isWfhEnabled =
    _.get(getClientConfigData(), 'wfh_config', {})?.enabled &&
    configDashboardSections.find(
      (section) => section.type === SECTION_TYPES.WFH_BOARD,
    );

  const activeSections = configDashboardSections
    .map((section) => {
      const updatedSection = { ...section };
      const sectionModules = updatedSection.modules || [];
      sectionModules.forEach((module) => {
        switch (module) {
          case MODULE_CODES.SETTINGS:
            settingsEnabled = true;
            if (updatedSection.name === SECTION_NAMES.MANAGE_GROUPS) {
              updatedSection.type = SECTION_TYPES.MANAGE_GROUPS;
            }
            break;
          case MODULE_CODES.OTHERS:
            ddpReports = true;
            break;
          case MODULE_CODES.REPORTS:
            reportsEnabled = true;
            break;
          case MODULE_CODES.TABLEAU:
            tableauEnabled = true;
            break;
          case MODULE_CODES.CUSTOMIZE:
            customizeEnabled = true;
            break;
          case MODULE_CODES.APPROVAL:
            approvalEnabled = true;
            break;
          case MODULE_CODES.ONBOARD:
            onboardEnabled = true;
            break;
          case MODULE_CODES.WAIVER_MATRIX:
            waiverMatrixEnabled = true;
            break;
          default:
            break;
        }
      });
      if (!sectionModules.length) {
        commonEnabled = true;
      }
      const sectionModuleCode = _.get(updatedSection, ['modules', '0']);
      if (
        updatedSection.type === SECTION_TYPES.BO_LIST &&
        campaignCodes.includes(sectionModuleCode)
      ) {
        const campaign = campaigns.find((c) => c.code === sectionModuleCode);
        modules.push(campaign);
      }
      if (updatedSection.type === SECTION_TYPES.ACTIVITY_BULK_UPLOAD) {
        activityBulkUploadEnabled = true;
      }
      return updatedSection;
    })
    .filter((section) => !_.isEmpty(section));

  if (shouldShowGoalsTab()) {
    modules.push(
      uiModules.find((module) => module.code === MODULE_CODES.GOAL_MANAGEMENT),
    );
    activeSections.push(SECTION_CONFIGS.GOAL_BULK_UPLOAD());
  }

  if (shouldShowHomeDashboard()) {
    modules.unshift(
      uiModules.find((module) => module.code === MODULE_CODES.HOME),
    );
    activeSections.push({
      ...SECTION_CONFIGS.HOME_PAGE(),
      path: HOMEPAGE_PATH,
    });
  }

  if (isSupersetEnabled) {
    modules.push(
      uiModules.find(
        (module) => module.code === MODULE_CODES.SUPERSET_DASHBOARD,
      ),
    );
  }

  if (approvalEnabled) {
    modules.push(
      uiModules.find((module) => module.code === MODULE_CODES.APPROVAL),
    );
  }

  if (waiverMatrixEnabled) {
    modules.push(
      uiModules.find((module) => module.code === MODULE_CODES.WAIVER_MATRIX),
    );
  }

  if (isWfhEnabled) {
    modules.push(uiModules.find((module) => module.code === MODULE_CODES.WFH));
  }

  if (!_.isEmpty(masterCodes)) {
    modules.push(
      uiModules.find((module) => module.code === MODULE_CODES.MASTERS),
    );
    activeSections.push(SECTION_CONFIGS.MASTERS());
  }

  if (canEnableTaskSections()) {
    modules.push(
      uiModules.find((module) => module.code === MODULE_CODES.TASKS),
    );
    if (activityBulkUploadEnabled) {
      activeSections.push(SECTION_CONFIGS.ACTIVITIES_BULK_UPLOAD());
    }
  }

  if (settingsEnabled) {
    modules.push(
      uiModules.find((module) => module.code === MODULE_CODES.SETTINGS),
    );
  }

  if (commonEnabled) {
    modules.push(
      uiModules.find((module) => module.code === MODULE_CODES.COMMON),
    );
  }

  if (ddpReports) {
    modules.push(
      uiModules.find((module) => module.code === MODULE_CODES.OTHERS),
    );
  }

  if (reportsEnabled) {
    modules.push(
      uiModules.find((module) => module.code === MODULE_CODES.REPORTS),
    );
  }

  if (tableauEnabled) {
    modules.push(
      uiModules.find((module) => module.code === MODULE_CODES.TABLEAU),
    );
  }

  if (getBrowserWebViewSections()?.length) {
    modules.push(
      uiModules.find((module) => module.code === MODULE_CODES.RESOURCE_CENTER),
    );
  }

  if (customizeEnabled) {
    modules.push(
      uiModules.find((module) => module.code === MODULE_CODES.CUSTOMIZE),
    );
  }

  if (onboardEnabled) {
    modules.push(
      uiModules.find((module) => module.code === MODULE_CODES.ONBOARD),
    );
  }

  if (isClientHealthDashboardEnabled) {
    modules.push(
      uiModules.find(
        (module) => module.code === MODULE_CODES.CLIENT_HEALTH_DASHBOARD,
      ),
    );
    activeSections.push(SECTION_CONFIGS.CLIENT_HEALTH_DASHBOARD());
  }

  const moduleMenuMap = {};
  activeSections.forEach((section) => {
    const sectionModules = _.get(section, 'modules', [MODULE_CODES.COMMON]);
    sectionModules.forEach((sectionModule) => {
      moduleMenuMap[sectionModule] = [
        ...(moduleMenuMap[sectionModule] || []),
        section,
      ];
    });
  });

  const sideBarTree = modules.map((module) => ({
    name: module?.name,
    code: module?.code,
    key: module?.key || module.code,
    type: module?.type || 'leads',
    items:
      module.items ||
      (moduleMenuMap[module?.code] || []).map((item) => ({
        name: item.name,
        type: item.type,
        key: item.key || `${module.code}_${item.type}`,
        path: item.path || `${getItemPath(item.type, module.code)}`,
      })),
  }));

  const filteredSideBar = sideBarTree
    .map((menu) => {
      if (_.isEmpty(menu.items)) {
        return null;
      }
      if (menu.items.length === 1) return menu.items[0];

      return menu;
    })
    .filter(Boolean);

  return filteredSideBar;
};

export const checkTypeAndModule = (type: string, module: string) => {
  let newType = type;
  let newModule = module;

  if (!_.isEmpty(type)) {
    const [prefix, suffix] = type.split('.');

    if (suffix === TYPE_SUFFIXES.DETAILS) {
      newType = prefix;
    }

    if (prefix === SECTION_TYPES.MANAGE_USERS) {
      newType = SECTION_TYPES.MANAGE_USERS;
      newModule = MODULE_CODES.SETTINGS;
    }

    if (prefix === SECTION_TYPES.MANAGE_GROUPS) {
      newType = SECTION_TYPES.MANAGE_GROUPS;
      newModule = MODULE_CODES.SETTINGS;
    }
  }

  if (type === TYPE_PREFIXES.TASK_DETAILS) {
    newType = TYPE_PREFIXES.LEADS;
    newModule = MODULE_CODES.TASKS;
  }

  if (type === TYPE_PREFIXES.JOB_DETAILS) {
    newType = SECTION_TYPES.ACTIVITY_BULK_UPLOAD;
    newModule = MODULE_CODES.TASKS;
  }

  return { module: newModule, type: newType };
};

export const findItemByPath = (
  menuItems: ExtendedNavigationItem[],
  path: string,
): ExtendedNavigationItem | null => {
  const foundItem = menuItems.find(
    (item) => (item as ExtendedNavigationItem).path === path,
  ) as ExtendedNavigationItem | undefined;
  if (foundItem) return foundItem;

  return (
    menuItems
      .filter((item) => item.items)
      .map((item) => findItemByPath(item.items!, path))
      .find((result) => result !== null) || null
  );
};

export const buildPathMap = (
  items: ExtendedNavigationItem[],
  map = new Map<string, ExtendedNavigationItem>(),
) => {
  items.forEach((item) => {
    if (item.path) {
      map.set(item.path, item);
    }

    if (item.items?.length) {
      buildPathMap(item.items, map);
    }
  });

  return map;
};

export const handleModuleNavigation = (
  item: ExtendedNavigationItem,
  dispatch: AppDispatch,
) => {
  const moduleId: ModuleTypeValue = item.moduleId || HOME_DASHBOARD;
  if (item.path) {
    window.location.hash = item.path;
  } else {
    dispatch(renderModule({ id: moduleId, props: {} }));
  }
};

export const getGroupedItems = (items = []) => {
  const typeToGroupMap = Object.entries(sidebarGroupings).reduce(
    (acc, [group, types]) => {
      types.forEach((type) => {
        acc[type] = group;
      });
      return acc;
    },
    {},
  );

  const groupedItems = Object.values(MENU_GROUP_TYPES).reduce((acc, group) => {
    acc[group] = [];
    return acc;
  }, {});

  items.forEach((item: any) => {
    const group = typeToGroupMap[item.type] ?? MENU_GROUP_TYPES.MORE;

    groupedItems[group].push(item);
  });

  return groupedItems;
};

const getParentPath = (currentPath, mapping) => {
  const matchingPattern = Object.keys(mapping).find((pattern) =>
    matchPath({ path: pattern, end: true }, currentPath),
  );

  if (!matchingPattern) return null;
  const match: any = matchPath(
    { path: matchingPattern, end: true },
    currentPath,
  );
  const { params } = match;
  const parentTemplate = mapping[matchingPattern];
  const resolvedParentPath = parentTemplate.replace(
    /:(\w+)/g,
    (placeholder, key) => params[key] || placeholder,
  );

  return resolvedParentPath;
};

export const queryMenuWithPath = (items, path) => {
  const getMenuFromPath = (pathname) => {
    if (!pathname) return null;
    // eslint-disable-next-line no-restricted-syntax
    for (const menu of items) {
      if (menu.items?.length) {
        const matchedMenu = menu.items.find(
          (childMenu) => childMenu.path === pathname,
        );
        if (matchedMenu) {
          return menu;
        }
      } else if (menu?.path === pathname) {
        return menu;
      }
    }
    return null;
  };

  const matchedItem = getMenuFromPath(path);

  if (matchedItem) return { menu: matchedItem, type: 'direct' };

  const possibleParentPath = getParentPath(path, subMenuMap);

  const parentItem = getMenuFromPath(possibleParentPath);

  return { menu: parentItem, type: 'parent' };
};
